/**
 * SettingsState
 * Stores the merchant and payment intent settings fetched during SDK init,
 * and exposes lookups derived from them.
 */

import type { TransactionSettings } from '@anddone/ajs-util';
import { createLogger } from '@anddone/ajs-logger';

const logger = createLogger('SDK', 'SettingsState');

export class SettingsState {
  // Populated once after the settings endpoint responds; deep-frozen on write.
  // TransactionSettings properties stored here (sourced from GET /fragments/settings):
  //   allowedDomains               – array<string>   (deep-frozen)
  //   allowedTransactionTypes      – array<object>   (deep-frozen, each item frozen)
  //   amount                       – number
  //   customers                    – array<object>   (deep-frozen, each item frozen)
  //   isAchValidationEnabled       – boolean
  //   merchantBranding             – object          (deep-frozen)
  //   paymentAuthorizationStatement– array<object>   (deep-frozen, each item frozen)
  //   paymentDescription           – string?         (primitive; carried via spread)
  //   referenceDataList            – array<object>?  (deep-frozen, each item frozen; omitted when absent)
  //   technologyFees               – array<object>   (deep-frozen, each item and its applicableTo array frozen)
  #current: TransactionSettings | null = null;
  #allowedChannelTypes: ReadonlySet<string> = new Set();

  /**
   * Settings fetched during SDK initialization, or null until the settings
   * endpoint has responded.
   */
  public get current(): TransactionSettings | null {
    return this.#current;
  }

  /**
   * Channel types listed in allowedTransactionTypes. Empty until settings load.
   */
  public get allowedChannelTypes(): ReadonlySet<string> {
    return this.#allowedChannelTypes;
  }

  /**
   * Whether a channel type is listed in allowedTransactionTypes.
   *
   * @param channelType - Channel type to check (e.g. 'credit', 'ach')
   * @returns boolean
   */
  public isChannelAllowed(channelType: string): boolean {
    return this.#allowedChannelTypes.has(channelType);
  }

  /**
   * Persist settings returned by the settings endpoint.
   * May only be called once; throws if called again to prevent overwriting.
   * All nested objects and arrays are deep-frozen so no external code can
   * mutate the stored values.
   *
   * @param settings - TransactionSettings from the API response
   */
  public set(settings: TransactionSettings): void {
    if (this.#current !== null) {
      throw new Error('TransactionSettings have already been initialized.');
    }

    this.#current = Object.freeze({
      ...settings,
      allowedDomains: Object.freeze([...settings.allowedDomains]),
      allowedTransactionTypes: Object.freeze(
        settings.allowedTransactionTypes.map(t => Object.freeze({ ...t })),
      ),
      customers: Object.freeze(settings.customers.map(c => Object.freeze({ ...c }))),
      merchantBranding: Object.freeze({ ...settings.merchantBranding }),
      paymentAuthorizationStatement: Object.freeze(
        settings.paymentAuthorizationStatement.map(p => Object.freeze({ ...p })),
      ),
      referenceDataList: settings.referenceDataList
        ? Object.freeze(settings.referenceDataList.map(r => Object.freeze({ ...r })))
        : undefined,
      technologyFees: Object.freeze(
        settings.technologyFees.map(f =>
          Object.freeze({ ...f, applicableTo: Object.freeze([...f.applicableTo]) }),
        ),
      ),
    });

    this.#allowedChannelTypes = new Set(
      this.#current.allowedTransactionTypes.map(t => t.channelType),
    );

    logger.info('settings', 'TransactionSettings initialized');
  }
}

Object.freeze(SettingsState.prototype);
